import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House, Users, Truck, Wrench, Package, ShoppingCart, Receipt,
  ArrowsClockwise, ClipboardText, ChartBar, SignOut, Warning, ArrowsLeftRight, Wallet, Gear, FileText, Lock
} from "@phosphor-icons/react";

const nav = [
  { to: "/", label: "Dashboard", icon: House, testid: "sidebar-nav-dashboard" },
  { section: "Masters" },
  { to: "/customers", label: "Customers", icon: Users, testid: "sidebar-nav-customers" },
  { to: "/vendors", label: "Vendors", icon: Truck, testid: "sidebar-nav-vendors" },
  { to: "/technicians", label: "Technicians", icon: Wrench, testid: "sidebar-nav-technicians" },
  { to: "/items", label: "Items / Parts", icon: Package, testid: "sidebar-nav-items" },
  { section: "Transactions" },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "sidebar-nav-quotations" },
  { to: "/purchases", label: "Purchase Entry", icon: ShoppingCart, testid: "sidebar-nav-purchases" },
  { to: "/sales", label: "Sales / Invoices", icon: Receipt, testid: "sidebar-nav-sales" },
  { to: "/tech-issue", label: "Technician Issue", icon: ArrowsLeftRight, testid: "sidebar-nav-tech-issue" },
  { to: "/tech-return", label: "Technician Return", icon: ArrowsClockwise, testid: "sidebar-nav-tech-return" },
  { to: "/tech-ledger", label: "Technician Ledger", icon: Wallet, testid: "sidebar-nav-tech-ledger" },
  { section: "Reports" },
  { to: "/stock-report", label: "Stock Report", icon: ChartBar, testid: "sidebar-nav-stock-report" },
  { to: "/low-stock", label: "Low Stock Alert", icon: Warning, testid: "sidebar-nav-low-stock" },
  { to: "/stock-movements", label: "Stock Movements", icon: ClipboardText, testid: "sidebar-nav-movements" },
  { section: "Setup" },
  { to: "/settings", label: "Company & Bank", icon: Gear, testid: "sidebar-nav-settings" },
  { to: "/profile", label: "Change Password", icon: Lock, testid: "sidebar-nav-profile" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 fixed left-0 top-0 h-screen bg-[hsl(222.2_47.4%_11.2%)] text-[hsl(210_40%_98%)] flex flex-col z-30">
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <img src="/logo.png"
               alt="Shree Ram Enterprises"
               className="w-10 h-10 rounded-full object-cover mr-3 bg-white" />
          <div>
            <div className="font-display font-bold text-sm leading-tight">Shree Ram</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Enterprises ERP</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {nav.map((n, i) =>
            n.section ? (
              <div key={i} className="px-6 pt-4 pb-1 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
                {n.section}
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `sidebar-item flex items-center px-6 py-2.5 text-sm border-l-[3px] border-transparent hover:bg-white/5 transition-colors ${
                    isActive ? "active text-white" : "text-white/70"
                  }`
                }
              >
                <n.icon size={18} weight="regular" className="mr-3 shrink-0" />
                <span>{n.label}</span>
              </NavLink>
            )
          )}
        </nav>
        <div className="border-t border-white/10 p-4 flex items-center justify-between">
          <div className="text-xs">
            <div className="font-medium">{user?.name || "Admin"}</div>
            <div className="text-white/50 truncate max-w-[140px]">{user?.email}</div>
          </div>
          <button
            data-testid="sidebar-logout-button"
            onClick={async () => { await logout(); nav_("/login"); }}
            className="p-2 rounded hover:bg-white/10"
            title="Logout"
          >
            <SignOut size={18} />
          </button>
        </div>
      </aside>

      {/* Topbar */}
      <header className="h-16 border-b border-border bg-card fixed top-0 left-64 right-0 z-20 flex items-center px-6 shadow-sm">
        <div className="text-sm text-muted-foreground">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </div>
        <div className="ml-auto text-sm">
          Welcome back, <span className="font-semibold">{user?.name || "Admin"}</span>
        </div>
      </header>

      {/* Main */}
      <main className="ml-64 pt-16 min-h-screen">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
